// @flow

import React, { useEffect } from 'react';
import { useCombobox } from 'downshift';
import { t } from 'javascripts/utils/withTranslation';
import classNames from 'classnames';
import Icon from 'components/_particles/icon/react/Icon';

export type Field = {
  city: string,
  rate: string,
};

export type Props = {
  data: Field[],
};

const PropertyTaxes = ({ data }: Props): React$MixedElement => {
  const [active, setActive] = React.useState<Field | void>();
  const [options, setOptions] = React.useState<Field[]>(data);

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    reset,
  } = useCombobox({
    onInputValueChange({ inputValue }) {
      setOptions(
        data?.filter((i) => {
          const lowerCasedInputValue = inputValue.toLowerCase();
          return (
            !inputValue || i.city.toLowerCase().startsWith(lowerCasedInputValue)
          );
        })
      );
    },
    items: options,
    itemToString(item) {
      return item ? item.city : '';
    },
  });

  useEffect(() => {
    if (selectedItem) {
      setActive(selectedItem);
      // clearing the item after selection so all options are shown again
      reset();
    }
  }, [selectedItem]);

  return (
    <>
      <div className="property-taxes__select-wrap">
        <label className="label" {...getLabelProps()}>
          {t('Kommune')}
        </label>
        <div className="property-taxes__grid">
          <div className="property-taxes__input-wrap">
            {active && !isOpen && (
              <span className="property-taxes__selected-text">
                {active.city}
              </span>
            )}
            <input
              placeholder={t('Kommune auswählen')}
              className={'select property-taxes__select'}
              {...getInputProps()}
            />
            <button
              aria-label={t('Menü öffnen und schließen')}
              className="button button--icon property-taxes__toggle"
              type="button"
              {...getToggleButtonProps()}
            >
              <Icon icon="arrow-down" />
            </button>
          </div>
        </div>
        <div
          className={classNames(
            'dropdown-menu',
            'property-taxes__menu',
            !(isOpen && options.length) && 'property-taxes__menu--hidden'
          )}
          {...getMenuProps()}
        >
          {isOpen && (
            <ul className="dropdown-menu__list">
              {options.map((item, index) => (
                <li
                  className={classNames(
                    'dropdown-menu__item',
                    'property-taxes__item',

                    selectedItem === item && 'property-taxes__item--selected'
                  )}
                  key={`${item.city}${index}`}
                  {...getItemProps({
                    item,
                    index,
                  })}
                >
                  <div
                    className={classNames(
                      'dropdown-menu__link',
                      highlightedIndex === index && 'current'
                    )}
                  >
                    <span className="dropdown-menu__text">{item.city}</span>
                  </div>
                </li>
              ))}
              {options.length === 0 && (
                <li className="dropdown-menu__item multiselect__empty">
                  {t('Nicht gefunden.')}
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
      <div role="region" aria-live="polite" className="property-taxes__area">
        <table className="table property-taxes__table">
          <thead>
            <tr>
              <th>{t('Kommune')}</th>
              <th>{t('Berechneter Hebesatz für die Grundsteuer B')}</th>
            </tr>
          </thead>
          <tbody>
            {active ? (
              <tr>
                <td>{active?.city}</td>
                <td>{active?.rate}</td>
              </tr>
            ) : (
              <tr>
                <td colSpan="2" className="property-taxes__placeholder">
                  {t('Nutzen Sie die Filterfunktion')}&nbsp;
                  <strong>{t('Kommune auswählen')}</strong>&nbsp;
                  {t('zur Anzeige der gewünschten Daten.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default PropertyTaxes;
